// The INVOICE STATUS section on a subcontractor's page (Session AVB). ADMIN ONLY: the page renders it only for an admin,
// as it does the rate card and welcome pack sections, because an invoice total is the money the subcontractor is paid and
// the locked AUU rule is that the office role does not read that money; the server refuses the read to anyone else.
//
// It shows EXACTLY what the subcontractor sees on the Invoices tab of their portal: the same object, built by the same
// server loader from the bills the office entered in Xero on the subcontractor's own payable contact. The one addition is
// for the office only: when the view is "not available", why, in words the office can act on (the subcontractor is told
// only that the status is not available). "Read Xero again" skips the five-minute cache.
//
// Read only: nothing here writes to the platform or to Xero. Shared components only (Button, Loader, StatusPill with the
// registry's tone and the server's own word, so "Approved, part paid" reads as the subcontractor reads it), dates through lib/format, no hand-typed CTA, no left stripe.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { SubInvoiceStatusOffice } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { formatDateTime } from "../../lib/format";
import { statusEntry } from "../../lib/statusRegistry";

export function SubcontractorInvoiceStatusSection({ subId }: { subId: string }) {
  const [data, setData] = useState<SubInvoiceStatusOffice | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    (fresh: boolean) => {
      let live = true;
      if (fresh) setBusy(true);
      setError(null);
      api.subcontractors
        .invoiceStatus(subId, fresh)
        .then((d) => {
          if (!live) return;
          setData(d);
          setState("ready");
        })
        .catch((e) => {
          if (!live) return;
          setError(friendlyApiError(e, "Could not load the invoice status. Please try again in a moment."));
          setState((s) => (s === "loading" ? "error" : s));
        })
        .finally(() => live && setBusy(false));
      return () => {
        live = false;
      };
    },
    [subId],
  );

  useEffect(() => load(false), [load]);

  const view = data?.view ?? null;
  return (
    <section className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-label="Invoice status">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg uppercase tracking-wide text-ele-blue">Invoice status</h2>
        {state === "ready" ? (
          <Button size="sm" variant="secondary" loading={busy} onClick={() => load(true)}>
            Read Xero again
          </Button>
        ) : null}
      </div>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view ? (
        <p role="alert" className="text-sm text-ele-strong">{error ?? "Could not load the invoice status."}</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ele-body">
            Exactly what this subcontractor sees on the Invoices tab of their portal, read from the bills entered in Xero on their payable contact. Reference and total only.
          </p>
          {data?.officeNote ? (
            <p className="rounded-ele border border-ele-purple-accent bg-ele-base px-3 py-2 text-sm text-ele-strong">
              <span className="font-menu text-xs uppercase tracking-wide text-ele-blue">For the office: </span>
              {data.officeNote}
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-ele-strong">
              {error}
            </p>
          ) : null}
          <div className="rounded-ele border border-ele-purple-accent bg-ele-base p-3" aria-label="What the subcontractor sees">
            <p className="text-sm text-ele-body">{view.message}</p>
            {view.invoices.length > 0 ? (
              <table className="ele-list-table mt-3 w-full text-left text-sm">
                <thead className="font-menu text-xs uppercase tracking-wide text-ele-blue">
                  <tr>
                    <th className="py-1 pr-2">Their reference</th>
                    <th className="py-1 pr-2">Total</th>
                    <th className="py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {view.invoices.map((r, i) => (
                    <tr key={`${r.reference ?? "none"}-${i}`} className="border-t border-ele-purple-accent align-top">
                      <td className="py-1.5 pr-2 text-ele-strong">{r.reference ?? "No reference recorded"}</td>
                      <td className="py-1.5 pr-2 text-ele-strong">{r.totalLabel ?? "Not available"}</td>
                      <td className="py-1.5">
                        <StatusPill word={r.label} tone={statusEntry("subcontractorInvoice", r.state).tone} />
                        <span className="mt-1 block text-xs text-ele-body">{r.detail}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {view.truncated ? <p className="mt-2 text-xs text-ele-body">Only the 100 most recent invoices are shown.</p> : null}
            {view.readAt ? <p className="mt-2 text-xs text-ele-body">Read from Xero at {formatDateTime(view.readAt)}.</p> : null}
          </div>
        </div>
      )}
    </section>
  );
}
